import React, { useMemo, useEffect, useState } from 'react';
import { Row, Col, Card, Typography, Tag, Button, Modal, Input, message } from 'antd';
import { Registro } from '../types';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import DateFilter from './DateFilter';
import { useFilteredRegistros } from '../hooks/useFilteredRegistros';
import {
  ReloadOutlined,
  PictureOutlined,
  CalendarOutlined,
  LeftOutlined,
  RightOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
  MailOutlined,
} from '@ant-design/icons';
import { sendIncidentEmail } from '../services/emailService';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Box from '@mui/material/Box';
import LazyImage from './LazyImage';
import AdvancedSearch from './AdvancedSearch';
import { useExportData } from '../hooks/useExportData';

dayjs.locale('es');
dayjs.extend(customParseFormat);

interface DashboardProps {
  registros: Registro[];
  onReload: () => void;
}

const getColumns = () => {
  if (window.innerWidth < 900) return 1;
  if (window.innerWidth < 1200) return 2;
  if (window.innerWidth < 1600) return 3;
  return 4;
};

const Dashboard: React.FC<DashboardProps> = ({ registros, onReload }) => {
  const [modalVisible, setModalVisible] = React.useState(false);
  const [currentImageIndex, setCurrentImageIndex] = React.useState(0);
  const [searchFilteredRegistros, setSearchFilteredRegistros] = useState<Registro[]>([]);
  const [emailModalVisible, setEmailModalVisible] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState<Registro | null>(null);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);

  const {
    periodo,
    setPeriodo,
    dateRange,
    setDateRange,
    registrosFiltrados,
    ordenAscendente,
    setOrdenAscendente,
  } = useFilteredRegistros(registros);

  const { exportToCSV, exportToPDF } = useExportData();

  // Usar registros filtrados por búsqueda si existen, sino usar los filtrados por fecha
  const finalRegistros =
    searchFilteredRegistros.length > 0 ? searchFilteredRegistros : registrosFiltrados;

  // Agrupar registros por fecha (orden descendente)
  const agruparPorFecha = (registros: Registro[]) => {
    const grupos = new Map<string, Registro[]>();
    registros
      .sort((a, b) =>
        ordenAscendente
          ? new Date(a.fecha).getTime() - new Date(b.fecha).getTime()
          : new Date(b.fecha).getTime() - new Date(a.fecha).getTime()
      )
      .forEach(registro => {
        const fecha = dayjs(registro.fecha).format('YYYY-MM-DD');
        if (!grupos.has(fecha)) {
          grupos.set(fecha, []);
        }
        grupos.get(fecha)?.push(registro);
      });
    return grupos;
  };

  const registrosPorFecha = agruparPorFecha(finalRegistros);

  // Masonry layout mejorado: 3 columnas (móvil/tablet), 4 columnas (desktop)
  const [columns, setColumns] = React.useState(getColumns());
  React.useEffect(() => {
    const handleResize = () => setColumns(getColumns());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Para mostrar los títulos de fecha
  const fechas = Array.from(registrosPorFecha.keys());

  // Card width y layout fijo
  const cardMinWidth = 320;
  const cardMaxWidth = 420;

  // --- NUEVO: Modal clásico para imágenes de cards ---
  const imagesList = useMemo(() => finalRegistros.filter(r => r.fotoUrl), [finalRegistros]);

  const openModal = (idx: number) => {
    setCurrentImageIndex(idx);
    setModalVisible(true);
  };

  const closeModal = () => {
    setModalVisible(false);
  };

  const showPrev = () => {
    setCurrentImageIndex(prev => (prev - 1 + imagesList.length) % imagesList.length);
  };

  const showNext = () => {
    setCurrentImageIndex(prev => (prev + 1) % imagesList.length);
  };

  // Manejo de teclado para navegación
  useEffect(() => {
    if (!modalVisible) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        showPrev();
      } else if (e.key === 'ArrowRight') {
        showNext();
      } else if (e.key === 'Escape') {
        closeModal();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [modalVisible, imagesList.length]);

  // Obtener el registro actual para mostrar su info en el modal
  const currentRegistro = imagesList[currentImageIndex];

  // --- NUEVO: Pasar correctamente los setters para rango personalizado ---
  // Para DateFilter necesitamos pasar setPeriodo y setDateRange correctos
  const handleRangeStart = (date: dayjs.Dayjs) => {
    setDateRange({ startDate: date.format('YYYY-MM-DD'), endDate: dateRange.endDate });
    setPeriodo('Personalizado');
  };
  const handleRangeEnd = (date: dayjs.Dayjs) => {
    setDateRange({ startDate: dateRange.startDate, endDate: date.format('YYYY-MM-DD') });
    setPeriodo('Personalizado');
  };
  const handleDateChange = (start: dayjs.Dayjs, end: dayjs.Dayjs) => {
    setDateRange({ startDate: start.format('YYYY-MM-DD'), endDate: end.format('YYYY-MM-DD') });
    setPeriodo('Personalizado');
  };

  // Función para cambiar el orden de los registros
  const cambiarOrden = () => {
    setOrdenAscendente(!ordenAscendente);
  };

  const [showScrollTop, setShowScrollTop] = React.useState(false);

  React.useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleExport = (filtered: Registro[]) => {
    exportToPDF(filtered, `registros_${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  // Función para abrir el modal de envío de email
  const handleOpenEmailModal = (incident: Registro) => {
    setSelectedIncident(incident);
    setRecipientEmail('');
    setEmailModalVisible(true);
  };

  // Función para enviar el email
  const handleSendEmail = async () => {
    if (!selectedIncident || !recipientEmail) {
      message.error('Por favor ingrese un email válido');
      return;
    }

    setSendingEmail(true);
    try {
      await sendIncidentEmail(selectedIncident, recipientEmail);
      message.success('Email enviado correctamente');
      setEmailModalVisible(false);
      setRecipientEmail('');
    } catch (error: any) {
      message.error(error.message || 'Error al enviar el email');
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <div style={{ padding: 0 }}>
      {/* Búsqueda Avanzada */}
      <AdvancedSearch
        registros={registrosFiltrados}
        onFilteredResults={setSearchFilteredRegistros}
        onExport={handleExport}
      />

      <DateFilter
        registros={registros}
        registrosFiltrados={registrosFiltrados}
        periodo={periodo}
        setPeriodo={setPeriodo}
        dateRange={dateRange}
        setRangeStart={handleRangeStart}
        setRangeEnd={handleRangeEnd}
        onDateChange={handleDateChange}
      />
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Typography.Title
            level={2}
            style={{
              fontWeight: 'bold',
              margin: 0,
              background: '#1890ff',
              padding: '4px 12px',
              borderRadius: 12,
              color: 'white',
            }}
          >
            PH Quatrium
          </Typography.Title>
        </Col>
        <Col>
          <Button
            type="primary"
            icon={<ReloadOutlined />}
            onClick={onReload}
            style={{ minWidth: 120 }}
          >
            Actualizar
          </Button>
          <Button
            type="primary"
            icon={ordenAscendente ? <SortAscendingOutlined /> : <SortDescendingOutlined />}
            onClick={cambiarOrden}
            style={{ minWidth: 120, marginLeft: 8 }}
          >
            {ordenAscendente ? 'Ascendente' : 'Descendente'}
          </Button>
        </Col>
      </Row>
      {fechas.map((fecha, idx) => {
        const registrosDelDia = registrosPorFecha.get(fecha) || [];
        // Distribuir registros de este día en columnas
        const cols: Registro[][] = Array.from({ length: columns }, () => []);
        registrosDelDia.forEach((r, i) => cols[i % columns].push(r));
        return (
          <div key={fecha} style={{ marginBottom: 36 }}>
            <div style={{ marginBottom: 18, marginTop: idx === 0 ? 0 : 18 }}>
              <span
                style={{
                  color: '#232b3b',
                  background: 'linear-gradient(90deg, #bfc7cf 70%, #e2e6ea 100%)',
                  borderRadius: 12,
                  padding: '2px 18px',
                  fontWeight: 600,
                  fontSize: 18,
                  fontFamily: 'Roboto, Helvetica Neue, Arial, sans-serif',
                  letterSpacing: 0.2,
                  boxShadow: '0 1px 6px rgba(0,0,0,0.08)',
                  display: 'inline-block',
                }}
              >
                {dayjs(fecha).format('dddd, D [de] MMMM [de] YYYY')}
              </span>
            </div>
            <div
              style={{
                display: 'flex',
                gap: 16,
                alignItems: 'flex-start',
                width: '100%',
                justifyContent: 'center',
              }}
            >
              {cols.map((col, colIdx) => (
                <div
                  key={colIdx}
                  style={{
                    flex: 1,
                    minWidth: cardMinWidth,
                    maxWidth: cardMaxWidth,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 24,
                  }}
                >
                  {col.map((registro, idx) => (
                    <Card
                      key={registro.id + '-' + idx}
                      style={{
                        borderRadius: 14,
                        background: '#232b3b',
                        color: 'white',
                        border: 'none',
                        boxShadow: '0 2px 8px rgba(24,144,255,0.09)',
                        width: '100%',
                        margin: 0,
                        padding: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'stretch',
                        overflow: 'hidden',
                        minWidth: cardMinWidth,
                        maxWidth: cardMaxWidth,
                      }}
                      hoverable
                      cover={
                        registro.fotoUrl ? (
                          <LazyImage
                            alt="Foto del registro"
                            src={registro.fotoUrl}
                            style={{
                              width: '100%',
                              height: 140,
                              objectFit: 'cover',
                              borderRadius: '14px 14px 0 0',
                              background: '#f5f5f5',
                              cursor: 'pointer',
                            }}
                            onClick={() => {
                              const idx = imagesList.findIndex(
                                img => img.fotoUrl === registro.fotoUrl
                              );
                              if (idx !== -1) openModal(idx);
                            }}
                          />
                        ) : (
                          <div
                            style={{
                              width: '100%',
                              height: 140,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              background: '#f5f5f5',
                              borderRadius: '14px 14px 0 0',
                            }}
                          >
                            <PictureOutlined style={{ fontSize: 44, color: '#bbb' }} />
                          </div>
                        )
                      }
                    >
                      <div
                        style={{
                          padding: '18px 16px 12px 16px',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 8,
                        }}
                      >
                        <Typography.Title
                          level={5}
                          style={{
                            margin: 0,
                            color: 'white',
                            fontWeight: 700,
                            fontSize: 18,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {registro.lugar}
                        </Typography.Title>
                        <Typography.Text
                          style={{
                            color: '#b0cfff',
                            fontSize: 15,
                            marginLeft: 0,
                            minWidth: 70,
                            fontWeight: 500,
                          }}
                        >
                          <CalendarOutlined style={{ marginRight: 4 }} />
                          {dayjs(registro.fecha).format('hh:mm A')}
                        </Typography.Text>
                        <Tag
                          color={
                            registro.incidencia &&
                            registro.incidencia.toLowerCase().includes('sin novedad')
                              ? 'green'
                              : 'orange'
                          }
                          style={{ width: 'fit-content', marginTop: 2 }}
                        >
                          {registro.incidencia}
                        </Tag>
                        <Typography.Text
                          style={{
                            color: '#aaa',
                            fontSize: 14,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            marginTop: 6,
                          }}
                        >
                          {registro.usuario}
                        </Typography.Text>
                        
                        {/* Botón de enviar por email */}
                        <Button
                          type="primary"
                          icon={<MailOutlined />}
                          onClick={() => handleOpenEmailModal(registro)}
                          style={{
                            marginTop: 12,
                            width: '100%',
                            borderRadius: 8,
                          }}
                        >
                          Enviar por Email
                        </Button>
                      </div>
                    </Card>
                  ))}
                </div>
              ))}
            </div>
          </div>
        );
      })}
      <Modal
        open={modalVisible}
        onCancel={closeModal}
        footer={null}
        centered
        width={window.innerWidth > 900 ? 900 : '95%'}
        style={{ background: 'none', boxShadow: 'none' }}
        bodyStyle={{ padding: 0, background: 'none', boxShadow: 'none' }}
        maskStyle={{ background: 'rgba(30, 41, 59, 0.92)' }}
        closeIcon={<span style={{ color: '#fff', fontSize: 22 }}>&times;</span>}
        styles={{ content: { background: 'none', boxShadow: 'none' } }}
      >
        {imagesList.length > 0 && (
          <div
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 0 }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                minHeight: window.innerWidth > 900 ? 520 : 260,
              }}
            >
              <Button
                icon={<LeftOutlined />}
                onClick={showPrev}
                disabled={imagesList.length <= 1}
                style={{
                  marginRight: 12,
                  border: 'none',
                  background: 'transparent',
                  color: '#fff',
                  fontSize: 30,
                  boxShadow: 'none',
                  minWidth: 48,
                  minHeight: 48,
                  borderRadius: 24,
                  transition: 'background 0.2s',
                }}
                tabIndex={-1}
              />
              <img
                src={currentRegistro.fotoUrl}
                alt={currentRegistro.descripcion || 'Imagen'}
                style={{
                  maxHeight: window.innerWidth > 900 ? '68vh' : '40vh',
                  maxWidth: window.innerWidth > 900 ? 720 : '90vw',
                  objectFit: 'contain',
                  margin: 'auto',
                  borderRadius: 16,
                  background: 'none',
                  boxShadow: 'none',
                  display: 'block',
                }}
              />
              <Button
                icon={<RightOutlined />}
                onClick={showNext}
                disabled={imagesList.length <= 1}
                style={{
                  marginLeft: 12,
                  border: 'none',
                  background: 'transparent',
                  color: '#fff',
                  fontSize: 30,
                  boxShadow: 'none',
                  minWidth: 48,
                  minHeight: 48,
                  borderRadius: 24,
                  transition: 'background 0.2s',
                }}
                tabIndex={-1}
              />
            </div>
            {/* Info del registro */}
            <div
              style={{
                marginTop: 18,
                textAlign: 'center',
                color: '#fff',
                padding: '0 16px 16px 16px',
                background: 'none',
              }}
            >
              <Typography.Title
                level={5}
                style={{
                  color: '#fff',
                  marginBottom: 2,
                  fontWeight: 700,
                  fontSize: 22,
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  background: 'none',
                }}
              >
                {currentRegistro.lugar}
              </Typography.Title>
              <Typography.Text
                style={{
                  color: '#b0cfff',
                  fontSize: 16,
                  marginLeft: 0,
                  minWidth: 70,
                  fontWeight: 500,
                  display: 'block',
                  background: 'none',
                }}
              >
                <CalendarOutlined style={{ marginRight: 4 }} />
                {dayjs(currentRegistro.fecha).format('dddd, D [de] MMMM [de] YYYY, hh:mm A')}
              </Typography.Text>
              <Tag
                color={
                  currentRegistro.incidencia &&
                  currentRegistro.incidencia.toLowerCase().includes('sin novedad')
                    ? 'green'
                    : 'orange'
                }
                style={{
                  width: 'fit-content',
                  marginTop: 8,
                  fontSize: 16,
                  fontWeight: 500,
                  background: 'none',
                }}
              >
                {currentRegistro.incidencia}
              </Tag>
              <Typography.Text
                style={{
                  color: '#aaa',
                  fontSize: 15,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  marginTop: 8,
                  display: 'block',
                  background: 'none',
                }}
              >
                {currentRegistro.usuario}
              </Typography.Text>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal para enviar incidencia por email */}
      <Modal
        title="📧 Enviar Incidencia por Email"
        open={emailModalVisible}
        onOk={handleSendEmail}
        onCancel={() => setEmailModalVisible(false)}
        okText="Enviar"
        cancelText="Cancelar"
        confirmLoading={sendingEmail}
        okButtonProps={{ disabled: !recipientEmail }}
      >
        <div style={{ marginBottom: 16 }}>
          <Typography.Text strong>Incidencia:</Typography.Text>
          <div style={{ marginTop: 8, padding: 12, background: '#f5f5f5', borderRadius: 8 }}>
            <Typography.Text>{selectedIncident?.lugar}</Typography.Text>
            <br />
            <Typography.Text type="secondary">
              {selectedIncident && dayjs(selectedIncident.fecha).format('DD/MM/YYYY HH:mm')}
            </Typography.Text>
            <br />
            <Tag
              color={
                selectedIncident?.incidencia &&
                selectedIncident.incidencia.toLowerCase().includes('sin novedad')
                  ? 'green'
                  : 'orange'
              }
              style={{ marginTop: 8 }}
            >
              {selectedIncident?.incidencia}
            </Tag>
          </div>
        </div>
        <div>
          <Typography.Text strong>Email(s) del destinatario:</Typography.Text>
          <Input
            placeholder="ejemplo@correo.com o varios separados por comas"
            value={recipientEmail}
            onChange={(e) => setRecipientEmail(e.target.value)}
            style={{ marginTop: 8 }}
            prefix={<MailOutlined />}
          />
          <Typography.Text type="secondary" style={{ fontSize: 12, marginTop: 4, display: 'block' }}>
            Puede ingresar múltiples emails separados por comas
          </Typography.Text>
        </div>
      </Modal>

      {/* Botón flotante para volver arriba */}
      {showScrollTop && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 32,
            right: 32,
            zIndex: 1200,
            bgcolor: 'primary.main',
            color: 'white',
            borderRadius: '50%',
            boxShadow: 3,
            width: 56,
            height: 56,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'opacity 0.3s',
            opacity: 0.85,
            '&:hover': { opacity: 1, bgcolor: 'primary.dark' },
          }}
          onClick={scrollToTop}
          title="Volver al inicio"
        >
          <KeyboardArrowUpIcon sx={{ fontSize: 36 }} />
        </Box>
      )}
    </div>
  );
};

export default Dashboard;
